export type AgentToolMeta = {
	label: string;
	hint: string;
};

export const BOARD_TOOL_META: Record<string, AgentToolMeta> = {
	presentOptions: {
		label: "Present options",
		hint: "Present selectable options for the user to pick from",
	},
	listImageModels: {
		label: "List image models",
		hint: "List Image Maker models available for in-chat generation",
	},
	generateImageInChat: {
		label: "Generate image",
		hint: "Generate image(s) in chat with selected Image Maker models",
	},
	loadSkill: {
		label: "Load skill",
		hint: "Load on-demand instructions for a catalog skill",
	},
	getBoardSnapshot: {
		label: "Get board snapshot",
		hint: "Get the latest board snapshot including visible and selected cards",
	},
	listVisibleCards: {
		label: "List visible cards",
		hint: "List visible cards in the current viewport with summaries",
	},
	listSelectedCards: {
		label: "List selected cards",
		hint: "List selected cards with readable content summaries",
	},
	getCardContent: {
		label: "Get card content",
		hint: "Get the full text content of a specific card by its card id",
	},
	openCard: {
		label: "Open card",
		hint: "Open a specific card without changing the camera",
	},
	navigateToCard: {
		label: "Navigate to card",
		hint: "Navigate the board viewport to a specific card",
	},
	createNoteCard: {
		label: "Create note card",
		hint: "Create a new note card in non-overlapping space near the viewport",
	},
	listPromptCards: {
		label: "List prompt cards",
		hint: "List all prompt cards on the current board",
	},
	listPreprintCards: {
		label: "List preprint cards",
		hint: "List all preprint (AI output) cards on the current board",
	},
	createPromptCard: {
		label: "Create prompt card",
		hint: "Create a new prompt card near the viewport",
	},
	generateFromPromptCard: {
		label: "Generate from prompt card",
		hint: "Generate AI output from a prompt card with one model",
	},
	generateFromPromptCardAllModels: {
		label: "Generate with all models",
		hint: "Generate with every generation-eligible model for the chosen agent",
	},
	listAvailableAgentsAndModels: {
		label: "List agents and models",
		hint: "List agents and models the user can generate with on this board",
	},
	fitBoardToContent: {
		label: "Fit board to content",
		hint: "Zoom and pan the board viewport to fit all content",
	},
	openBoardSearch: {
		label: "Open board search",
		hint: "Open the board quick-search panel",
	},
	listShapesInViewport: {
		label: "List shapes in viewport",
		hint: "List shapes in the current viewport with bounds and summaries",
	},
	listShapesOnPage: {
		label: "List shapes on page",
		hint: "List all shapes on the current page with enriched summaries",
	},
	listSelectedShapes: {
		label: "List selected shapes",
		hint: "List selected shapes with bounds and one-line summaries",
	},
	getArrowConnections: {
		label: "Get arrow connections",
		hint: "List all arrows with resolved fromShapeId and toShapeId bindings",
	},
	inspectDiagram: {
		label: "Inspect diagram",
		hint: "Review box overlaps, arrow collisions, and sketch connectivity metrics",
	},
	getShapeContent: {
		label: "Get shape content",
		hint: "Get readable content from any shape by shape id",
	},
	navigateToShape: {
		label: "Navigate to shape",
		hint: "Navigate the board viewport to a shape by shape id",
	},
	listCustomShapes: {
		label: "List custom shapes",
		hint: "List Illumi custom shapes (cards, sections, images) on the board",
	},
	getCustomShapeInfo: {
		label: "Get custom shape info",
		hint: "Get structured metadata for an Illumi custom shape",
	},
	findEmptySpace: {
		label: "Find empty space",
		hint: "Find a non-overlapping position for a rectangle of the given size",
	},
	createGeoShape: {
		label: "Create geo shape",
		hint: "Create a palette shape (rectangle, ellipse, diamond, etc.)",
	},
	createTextShape: {
		label: "Create text shape",
		hint: "Create standalone floating text at a position",
	},
	createArrow: {
		label: "Create arrow",
		hint: "Bind shapes; notes → prompt for Illumi context",
	},
	createDrawShape: {
		label: "Create draw stroke",
		hint: "Last-resort pencil; prefer geo+line for sketches",
	},
	createHighlightShape: {
		label: "Create highlight",
		hint: "Highlighter stroke for emphasis",
	},
	createLineShape: {
		label: "Create line",
		hint: "Lines for limbs, whiskers, polylines",
	},
	createNoteShape: {
		label: "Create sticky note",
		hint: "Create a sticky note at a position with text inside",
	},
	createFrameShape: {
		label: "Create frame",
		hint: "Create a frame region with optional title",
	},
	updateGeoShape: {
		label: "Update geo shape",
		hint: "Update a geo shape position, size, label, color, or type",
	},
	updateArrow: {
		label: "Update arrow",
		hint: "Update an arrow label and/or rebind endpoints",
	},
	updateTextShape: {
		label: "Update text shape",
		hint: "Update floating text position or content",
	},
	updateNoteShape: {
		label: "Update sticky note",
		hint: "Update sticky note position, text, or color",
	},
	updateFrameShape: {
		label: "Update frame",
		hint: "Update frame bounds, title, or color",
	},
	updateDrawShape: {
		label: "Update draw stroke",
		hint: "Update draw stroke points or style",
	},
	updateLineShape: {
		label: "Update line",
		hint: "Update line points or style",
	},
	updateHighlightShape: {
		label: "Update highlight",
		hint: "Update highlight stroke points or style",
	},
	updateImageShape: {
		label: "Update image shape",
		hint: "Update Illumi image shape bounds or URL",
	},
	moveShape: {
		label: "Move shape",
		hint: "Move a native canvas shape to a new position",
	},
	resizeShapes: {
		label: "Resize shapes",
		hint: "Resize canvas shapes by scale or size delta",
	},
	rotateShapes: {
		label: "Rotate shapes",
		hint: "Rotate canvas shapes by degrees or radians",
	},
	flipShapes: {
		label: "Flip shapes",
		hint: "Flip canvas shapes horizontally or vertically",
	},
	nudgeShapes: {
		label: "Nudge shapes",
		hint: "Nudge canvas shapes by dx and dy",
	},
	lockShapes: {
		label: "Lock shapes",
		hint: "Lock canvas shapes to prevent edits",
	},
	unlockShapes: {
		label: "Unlock shapes",
		hint: "Unlock previously locked canvas shapes",
	},
	setCamera: {
		label: "Set camera",
		hint: "Set board camera position and zoom",
	},
	getCamera: {
		label: "Get camera",
		hint: "Retrieve current board camera coordinates and zoom level",
	},
	selectShapes: {
		label: "Select shapes",
		hint: "Select shapes on the board by shape id",
	},
	moveCard: {
		label: "Move card",
		hint: "Move an Illumi card to a new position",
	},
	resizeCard: {
		label: "Resize card",
		hint: "Resize an Illumi card width and/or height",
	},
	updateCardContent: {
		label: "Update card content",
		hint: "Replace note or prompt card body text",
	},
	deleteCard: {
		label: "Delete card",
		hint: "Delete an Illumi card by card id",
	},
	duplicateCard: {
		label: "Duplicate card",
		hint: "Duplicate an Illumi card with a new card id",
	},
	lockCard: {
		label: "Lock card",
		hint: "Lock an Illumi card to prevent edits",
	},
	unlockCard: {
		label: "Unlock card",
		hint: "Unlock a locked Illumi card",
	},
	createCardSection: {
		label: "Create card section",
		hint: "Create a card section around one or more cards",
	},
	moveShapesToCardSection: {
		label: "Move to card section",
		hint: "Move cards into an existing card section",
	},
	runBoardPlan: {
		label: "Run board plan",
		hint: "Execute multiple board mutations in one undo step",
	},
	alignShapes: {
		label: "Align shapes",
		hint: "Align edges (not for unstacking — use distribute first)",
	},
	distributeShapes: {
		label: "Distribute shapes",
		hint: "Spread shapes evenly; unstacks coincident cards",
	},
	duplicateShapes: {
		label: "Duplicate shapes",
		hint: "Duplicate canvas shapes with a small offset",
	},
	groupShapes: {
		label: "Group shapes",
		hint: "Group two or more canvas shapes",
	},
	ungroupShapes: {
		label: "Ungroup shapes",
		hint: "Ungroup grouped canvas shapes",
	},
	moveShapesToFrame: {
		label: "Move shapes to frame",
		hint: "Move canvas shapes into a frame",
	},
	setShapeZOrder: {
		label: "Set shape z-order",
		hint: "Bring shapes to front or send them to back",
	},
	exportViewportImage: {
		label: "Export viewport image",
		hint: "Tight PNG crop of shape ids (not full page)",
	},
	getBoardDocumentSnapshot: {
		label: "Get board document snapshot",
		hint: "Get the full board document snapshot JSON",
	},
	createImageShape: {
		label: "Create image shape",
		hint: "Create an Illumi image shape from an http(s) URL",
	},
	deleteShape: {
		label: "Delete shape",
		hint: "Delete a canvas shape by shape id",
	},
	getCurrentRoute: {
		label: "Get current route",
		hint: "Get the current app page (pathname and parsed route)",
	},
	navigateApp: {
		label: "Navigate app",
		hint: "Navigate to an authenticated app page or board",
	},
	listNavbarTree: {
		label: "List navbar tree",
		hint: "List boards and folders in My Workspace",
	},
	createWhiteboard: {
		label: "Create whiteboard",
		hint: "Create a new board in My Workspace",
	},
	renameWhiteboard: {
		label: "Rename whiteboard",
		hint: "Rename a board in My Workspace",
	},
	createFolder: {
		label: "Create folder",
		hint: "Create a folder in My Workspace",
	},
	renameFolder: {
		label: "Rename folder",
		hint: "Rename a folder in My Workspace",
	},
	deleteFolder: {
		label: "Delete folder",
		hint: "Delete an empty folder from My Workspace",
	},
	moveNavbarItem: {
		label: "Move navbar item",
		hint: "Move a board or folder within My Workspace",
	},
	duplicateWhiteboard: {
		label: "Duplicate whiteboard",
		hint: "Duplicate a board in My Workspace",
	},
	deleteWhiteboard: {
		label: "Delete whiteboard",
		hint: "Move a board to trash (blocked when shared or published)",
	},
	"workspace.search": {
		label: "Search workspace",
		hint: "Search cards, boards, shapes, and people across workspace",
	},
	workspace_search: {
		label: "Search workspace",
		hint: "Search cards, boards, shapes, and people across workspace",
	},
	"workspace.get": {
		label: "Get workspace items",
		hint: "Get detailed content, markdown, and references for workspace items",
	},
	workspace_get: {
		label: "Get workspace items",
		hint: "Get detailed content, markdown, and references for workspace items",
	},
	graphql_query: {
		label: "GraphQL query",
		hint: "Run read-only GraphQL query on backend",
	},
	graphql_mutation: {
		label: "GraphQL mutation",
		hint: "Run GraphQL mutation on backend",
	},
	me: {
		label: "Current user",
		hint: "Fetch profile of currently authenticated user",
	},
	version: {
		label: "Backend version",
		hint: "Check backend server version",
	},
};

export const BOARD_TOOL_LABELS = Object.fromEntries(
	Object.entries(BOARD_TOOL_META).map(([name, meta]) => [name, meta.label]),
) as Record<keyof typeof BOARD_TOOL_META, string>;

export function getBoardToolMeta(toolName: string): AgentToolMeta {
	return (
		BOARD_TOOL_META[toolName] ?? {
			label: `Tool: ${toolName}`,
			hint: "Agent tool execution",
		}
	);
}

export function getBoardToolLabel(toolName: string): string {
	return getBoardToolMeta(toolName).label;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return null;
	}

	return value as Record<string, unknown>;
}

function truncate(value: string, maxLength = 40): string {
	if (value.length <= maxLength) {
		return value;
	}

	return `${value.slice(0, maxLength - 1)}…`;
}

function formatCount(count: number, singular: string, plural?: string): string {
	const label = count === 1 ? singular : (plural ?? `${singular}s`);
	return `${count} ${label}`;
}

export function summarizeToolInput(
	toolName: string,
	input: unknown,
): string | undefined {
	const record = asRecord(input);
	if (!record) {
		return undefined;
	}

	switch (toolName) {
		case "getCardContent":
		case "openCard":
		case "navigateToCard":
			return typeof record.cardId === "string"
				? truncate(record.cardId)
				: undefined;
		case "getShapeContent":
		case "navigateToShape":
		case "getCustomShapeInfo":
		case "deleteShape":
		case "moveShape":
		case "updateGeoShape":
		case "updateArrow":
		case "updateTextShape":
		case "updateNoteShape":
		case "updateFrameShape":
		case "updateDrawShape":
		case "updateLineShape":
		case "updateHighlightShape":
		case "updateImageShape":
			return typeof record.shapeId === "string"
				? truncate(record.shapeId)
				: undefined;
		case "moveCard":
		case "resizeCard":
		case "updateCardContent":
		case "deleteCard":
		case "duplicateCard":
		case "lockCard":
		case "unlockCard":
			return typeof record.cardId === "string"
				? truncate(record.cardId)
				: undefined;
		case "createNoteCard":
			return typeof record.title === "string"
				? truncate(record.title)
				: undefined;
		case "createPromptCard":
		case "generateFromPromptCard":
		case "generateFromPromptCardAllModels":
			if (typeof record.promptText === "string") {
				return truncate(record.promptText);
			}
			if (typeof record.prompt === "string") {
				return truncate(record.prompt);
			}
			if (typeof record.cardId === "string") {
				return truncate(record.cardId);
			}
			return undefined;
		case "createGeoShape":
			if (typeof record.text === "string" && record.text.length > 0) {
				return truncate(record.text);
			}
			if (typeof record.geo === "string") {
				return record.geo;
			}
			return undefined;
		case "createTextShape":
		case "createNoteShape":
			return typeof record.text === "string"
				? truncate(record.text)
				: undefined;
		case "createFrameShape":
			return typeof record.name === "string"
				? truncate(record.name)
				: undefined;
		case "createArrow":
			if (
				typeof record.fromShapeId === "string" &&
				typeof record.toShapeId === "string"
			) {
				return `${truncate(record.fromShapeId, 12)} → ${truncate(record.toShapeId, 12)}`;
			}
			return undefined;
		case "presentOptions": {
			const options = record.options;
			if (Array.isArray(options)) {
				return formatCount(options.length, "option");
			}
			return undefined;
		}
		case "listImageModels": {
			if (typeof record.offset === "number") {
				return `offset ${record.offset}`;
			}
			return undefined;
		}
		case "loadSkill":
			return typeof record.name === "string"
				? truncate(record.name)
				: undefined;
		case "generateImageInChat": {
			const modelIds = record.modelIds;
			if (Array.isArray(modelIds)) {
				return formatCount(modelIds.length, "model");
			}
			return typeof record.prompt === "string"
				? truncate(record.prompt)
				: undefined;
		}
		case "alignShapes":
		case "distributeShapes":
		case "duplicateShapes":
		case "groupShapes":
		case "ungroupShapes":
		case "moveShapesToFrame":
		case "setShapeZOrder":
		case "resizeShapes":
		case "rotateShapes":
		case "flipShapes":
		case "nudgeShapes":
		case "lockShapes":
		case "unlockShapes":
		case "selectShapes": {
			const shapeIds = record.shapeIds;
			if (Array.isArray(shapeIds)) {
				return formatCount(shapeIds.length, "shape");
			}
			return undefined;
		}
		case "createCardSection": {
			const cardIds = record.cardIds;
			if (Array.isArray(cardIds)) {
				return formatCount(cardIds.length, "card");
			}
			const shapeIds = record.shapeIds;
			if (Array.isArray(shapeIds)) {
				return formatCount(shapeIds.length, "card");
			}
			return undefined;
		}
		case "moveShapesToCardSection": {
			if (typeof record.sectionId === "string") {
				return truncate(record.sectionId);
			}
			return undefined;
		}
		case "runBoardPlan": {
			const steps = record.steps;
			if (Array.isArray(steps)) {
				return formatCount(steps.length, "step");
			}
			return undefined;
		}
		case "setCamera":
			if (
				typeof record.x === "number" &&
				typeof record.y === "number" &&
				typeof record.z === "number"
			) {
				return `${Math.round(record.x)},${Math.round(record.y)} @${record.z.toFixed(2)}`;
			}
			return undefined;
		case "findEmptySpace":
			if (
				typeof record.width === "number" &&
				typeof record.height === "number"
			) {
				return `${record.width}×${record.height}`;
			}
			return undefined;
		case "listShapesOnPage":
			return typeof record.type === "string" ? record.type : undefined;
		case "navigateApp":
			if (typeof record.destination === "string") {
				if (
					record.destination === "whiteboard" &&
					typeof record.whiteboardId === "string"
				) {
					return `whiteboard ${truncate(record.whiteboardId)}`;
				}
				if (
					record.destination === "card" &&
					typeof record.cardId === "string"
				) {
					return `card ${truncate(record.cardId)}`;
				}
				return record.destination;
			}
			return undefined;
		case "renameWhiteboard":
		case "renameFolder":
		case "createWhiteboard":
		case "createFolder":
			return typeof record.name === "string"
				? truncate(record.name)
				: undefined;
		case "moveNavbarItem":
			return typeof record.itemKey === "string"
				? truncate(record.itemKey)
				: undefined;
		case "deleteWhiteboard":
		case "duplicateWhiteboard":
			return typeof record.whiteboardId === "string"
				? truncate(record.whiteboardId)
				: undefined;
		case "deleteFolder":
			return typeof record.folderKey === "string"
				? truncate(record.folderKey)
				: undefined;
		case "workspace.search":
		case "workspace_search":
			return typeof record.query === "string"
				? truncate(record.query)
				: undefined;
		case "workspace.get":
		case "workspace_get": {
			const ids = record.ids ?? record.item_ids;
			if (Array.isArray(ids)) {
				return formatCount(ids.length, "item");
			}
			return undefined;
		}
		case "graphql_query":
		case "graphql_mutation":
			return typeof record.query === "string"
				? truncate(record.query.replace(/\s+/g, " ").trim())
				: undefined;
		default:
			return summarizeGenericInput(record);
	}
}

function summarizeGenericInput(
	record: Record<string, unknown>,
): string | undefined {
	for (const value of Object.values(record)) {
		if (typeof value === "string" && value.length > 0) {
			return truncate(value);
		}
	}

	return undefined;
}

export function summarizeToolResult(
	toolName: string,
	output: unknown,
): string | undefined {
	const record = asRecord(output);
	if (!record) {
		return undefined;
	}

	switch (toolName) {
		case "listVisibleCards":
		case "listSelectedCards":
		case "listPromptCards":
		case "listPreprintCards": {
			const cards = record.cards;
			if (Array.isArray(cards)) {
				return formatCount(cards.length, "card");
			}
			if (typeof record.cardCount === "number") {
				return formatCount(record.cardCount, "card");
			}
			return undefined;
		}
		case "listShapesInViewport":
		case "listShapesOnPage":
		case "listSelectedShapes":
		case "listCustomShapes": {
			const shapes = record.shapes;
			if (Array.isArray(shapes)) {
				return formatCount(shapes.length, "shape");
			}
			if (typeof record.shapeCount === "number") {
				return formatCount(record.shapeCount, "shape");
			}
			return undefined;
		}
		case "getCardContent":
			if (record.found === false) {
				return "Card not found";
			}
			return typeof record.typeLabel === "string"
				? record.typeLabel
				: "Card loaded";
		case "getShapeContent":
			if (record.found === false) {
				return "Shape not found";
			}
			return typeof record.typeLabel === "string"
				? record.typeLabel
				: "Shape loaded";
		case "listAvailableAgentsAndModels": {
			const agents = record.agents;
			if (Array.isArray(agents)) {
				return formatCount(agents.length, "agent");
			}
			return undefined;
		}
		case "presentOptions": {
			const options = record.options;
			if (Array.isArray(options)) {
				return formatCount(options.length, "option");
			}
			return "Options presented";
		}
		case "loadSkill":
			if (record.ok === false) {
				return "Unavailable";
			}
			return typeof record.name === "string" ? truncate(record.name) : "Loaded";
		case "listImageModels": {
			const models = record.models;
			if (Array.isArray(models)) {
				return formatCount(models.length, "model");
			}
			if (typeof record.total === "number") {
				return formatCount(record.total, "model");
			}
			return "Models listed";
		}
		case "generateImageInChat": {
			const results = record.results;
			if (Array.isArray(results)) {
				const okCount = results.filter(
					(item) =>
						typeof item === "object" &&
						item !== null &&
						(item as { status?: string }).status === "ok",
				).length;
				const errorCount = results.length - okCount;
				if (errorCount > 0 && okCount > 0) {
					return `${okCount} ok · ${errorCount} failed`;
				}
				if (errorCount > 0) {
					return formatCount(errorCount, "failed");
				}
				return formatCount(okCount, "image");
			}
			return "Generation finished";
		}
		case "createGeoShape":
		case "createTextShape":
		case "createArrow":
		case "createDrawShape":
		case "createHighlightShape":
		case "createLineShape":
		case "createNoteShape":
		case "createFrameShape":
		case "createImageShape":
		case "createNoteCard":
		case "createPromptCard":
			if (typeof record.shapeId === "string") {
				return "Created";
			}
			if (typeof record.cardId === "string") {
				return "Created";
			}
			return "Done";
		case "deleteShape":
			return record.deleted === true ? "Deleted" : "Done";
		case "deleteCard":
			return record.deleted === true ? "Deleted" : "Done";
		case "runBoardPlan": {
			if (typeof record.applied === "number") {
				return formatCount(record.applied, "step applied", "steps applied");
			}
			return "Plan executed";
		}
		case "duplicateCard":
			return record.duplicated === true ? "Duplicated" : "Done";
		case "getArrowConnections": {
			const arrows = record.arrows;
			if (Array.isArray(arrows)) {
				return formatCount(arrows.length, "arrow");
			}
			return undefined;
		}
		case "inspectDiagram": {
			const shapes = record.shapes;
			const detached =
				typeof record.detachedDrawPartCount === "number"
					? record.detachedDrawPartCount
					: 0;
			const sparse =
				typeof record.sparseDrawStrokeCount === "number"
					? record.sparseDrawStrokeCount
					: 0;
			if (Array.isArray(shapes)) {
				const base = formatCount(shapes.length, "shape");
				if (detached > 0 || sparse > 0) {
					return `${base} · ${detached} detached · ${sparse} sparse`;
				}
				return base;
			}
			return "Diagram reviewed";
		}
		case "generateFromPromptCard":
		case "generateFromPromptCardAllModels":
			return "Generation started";
		case "fitBoardToContent":
		case "openBoardSearch":
		case "getBoardSnapshot":
		case "findEmptySpace":
			return "Done";
		case "exportViewportImage":
			if (record.exported === false) {
				return "Export failed";
			}
			if (
				typeof record.width === "number" &&
				typeof record.height === "number"
			) {
				return `${record.width}×${record.height}px`;
			}
			return "Image exported";
		case "getCurrentRoute":
			return typeof record.route === "string" ? record.route : undefined;
		case "navigateApp":
			if (record.ok === false) {
				return "Failed";
			}
			return typeof record.pathname === "string"
				? truncate(record.pathname)
				: "Navigated";
		case "listNavbarTree":
			if (typeof record.itemCount === "number") {
				return formatCount(record.itemCount, "item");
			}
			return undefined;
		case "createWhiteboard":
		case "duplicateWhiteboard":
			return typeof record.whiteboardId === "string" ? "Created" : "Done";
		case "createFolder":
			return typeof record.folderKey === "string" ? "Created" : "Done";
		case "renameWhiteboard":
		case "renameFolder":
			return typeof record.name === "string"
				? truncate(record.name)
				: "Renamed";
		case "deleteFolder":
			return record.deleted === true ? "Deleted" : "Done";
		case "moveNavbarItem": {
			const movedKeys = record.movedKeys;
			if (Array.isArray(movedKeys)) {
				return formatCount(movedKeys.length, "item");
			}
			return "Moved";
		}
		case "deleteWhiteboard":
			if (record.deleted === false) {
				return "Blocked";
			}
			return record.deleted === true ? "Moved to trash" : "Done";
		case "workspace.search":
		case "workspace_search": {
			const hits = record.hits ?? record.items;
			if (Array.isArray(hits)) {
				return formatCount(hits.length, "hit");
			}
			return "Searched";
		}
		case "workspace.get":
		case "workspace_get": {
			const items = record.items;
			if (Array.isArray(items)) {
				return formatCount(items.length, "item");
			}
			return "Items loaded";
		}
		case "graphql_query":
		case "graphql_mutation":
			return record.errors ? "Error" : "Done";
		case "me":
			return "Profile loaded";
		case "version":
			return typeof record.version === "string" ? record.version : "Done";
		default:
			return summarizeGenericResult(record);
	}
}

function summarizeGenericResult(
	record: Record<string, unknown>,
): string | undefined {
	if (record.found === false) {
		return "Not found";
	}

	if (record.error) {
		return "Error";
	}

	if (typeof record.shapeId === "string") {
		return "Done";
	}

	if (typeof record.cardId === "string") {
		return "Done";
	}

	return undefined;
}
